import { FC, useRef, useState } from "react"
import { Alert, Stack } from "@mui/material"
import {
  TextFieldElement,
  SwitchElement,
  UseFormReturn,
} from "react-hook-form-mui"
import { RtkHookForm } from "@/core/components"
import { Userbot } from "../../interfaces"
import { useCreateRoleMutation, useUpdateRoleMutation } from "../../store"

interface IProps {
  role?: Userbot.Role
  onDone?: (role?: Userbot.Role) => void
}

interface IFormData {
  name: string
  description?: string
  isActive: boolean
  updatesEnabled: boolean
  catchUpEnabled: boolean
}

export const RoleForm: FC<IProps> = ({ role, onDone }) => {
  const isEdit = !!role

  const formContext = useRef<UseFormReturn<IFormData>>()
  const [defaultValues, setDefaultValues] = useState<IFormData>(() => ({
    name: role?.name ?? "",
    description: role?.description ?? "",
    isActive: role?.isActive ?? true,
    updatesEnabled: role?.updatesEnabled ?? false,
    catchUpEnabled: role?.catchUpEnabled ?? false,
  }))

  return (
    <RtkHookForm<IFormData, Userbot.Role>
      successMessage={isEdit ? "Роль обновлена" : "Роль создана"}
      mutationHook={
        (isEdit ? useUpdateRoleMutation : useCreateRoleMutation) as never
      }
      getSubmitHandlerParams={(data) => {
        if (isEdit) {
          const body: Userbot.UpdateRole = {}
          for (const key of [
            "name",
            "description",
            "isActive",
            "updatesEnabled",
            "catchUpEnabled",
          ] as const) {
            // Do not overwrite a flag that changed on the server while editing.
            if (data[key] !== defaultValues[key]) {
              Object.assign(body, { [key]: data[key] })
            }
          }
          return { id: role!.id, body }
        }
        const body: Userbot.CreateRole = {
          name: data.name,
          description: data.description || undefined,
          isActive: data.isActive,
          updatesEnabled: data.updatesEnabled,
          catchUpEnabled: data.catchUpEnabled,
        }
        return { body }
      }}
      setFormContext={(context) => {
        formContext.current = context
      }}
      afterSubmit={(res: Userbot.Role) => {
        if (isEdit) {
          const saved: IFormData = {
            name: res.name,
            description: res.description ?? "",
            isActive: res.isActive,
            updatesEnabled: res.updatesEnabled,
            catchUpEnabled: res.catchUpEnabled,
          }
          setDefaultValues(saved)
          formContext.current?.reset(saved)
        }
        onDone?.(res)
      }}
      formContextParams={{ defaultValues }}
      actions={[
        {
          text: "Сохранить",
          type: "submit",
          variant: "contained",
          fullWidth: true,
        },
      ]}
    >
      <Stack spacing={2}>
        <TextFieldElement<IFormData> name="name" label="Название" required />
        <TextFieldElement<IFormData>
          name="description"
          label="Описание"
          multiline
        />
        <SwitchElement<IFormData> name="isActive" label="Активна" />
        <SwitchElement<IFormData> name="updatesEnabled" label="Обновления" />
        <SwitchElement<IFormData> name="catchUpEnabled" label="Catch-up" />
        <Alert severity="info">
          Сохранённые разрешения независимы. Обновления разрешает любая активная
          роль с этим флагом; catch-up требует обоих флагов на одной активной
          роли. Флаги разных ролей не объединяются. Это не согласие на
          скачивание истории или анализ.
        </Alert>
      </Stack>
    </RtkHookForm>
  )
}
